import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { TIMELINE_FILTERS_TO_REMEMBER, type TicMemoryViewmode } from 'constants/user';
import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import type { SubscriptionMessageKeyTypes } from 'redux/modules/subscriptions.types';
import type { SyntheticState } from 'redux/modules/synthetic';
import { sliceName as syntheticSliceName } from 'redux/modules/synthetic.constants';
import type { Filter } from 'redux/modules/timeline.types';
import { TimelineStyleTypes } from 'redux/modules/timeline.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getProjectVariantMap, getRecentlyUsedProjectIds } from 'redux/selectors/projects';
import {
  getHasEnabledTic,
  getTicMemoryViewmode,
  getUserProperties,
  getUserTimeFormat,
} from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { TIMELINE_SCHEMA_KEY /* TIMELINE_PREDICTION_SCHEMA_KEY */ } from 'schema/timeline';
import type {
  Entry,
  SuggestedEntry,
  SuggestedEntryProject,
  SuggestedEntryWithProjects,
} from 'types/timeline';
import { notNull, pick } from 'utils/typesafe';
import { getCurrentAccount, getIsSubscriptionExpired, getIsTrialEnded } from './accounts';
import { getInjectSyntheticData } from './development';
import { getPinnedProjectIdsSet } from './pins';
import { getUpgradePermissionsInfo } from './subscriptions';
import { enrichProjectVariant } from './utils/projects';

const readGroups = (state: IState) => state.timeline.groups;
const readFilter = (state: IState) => state.timeline.filter;
const readXhrStatus = (state: IState) => state.timeline.xhrStatus;
const readTimelineEntities = (state: IState) => state.timeline.entities[TIMELINE_SCHEMA_KEY];
const readTimelineResult = (state: IState) => state.timeline.result;
const readTimelineProjectPredictions = (state: IState) => state.timeline.projectPredictions;
const readTimelineEntryIdPredictions = (state: IState) => state.timeline.entryIdPredictions;
const readTimelineSelection = (state: IState) => state.timeline.selection;
const readScrollPosition = (state: IState) => state.timeline.scrollPostion;
const readTimelineStyle = (state: IState) => state.timeline.style;
const readTimelineGutterHighlight = (state: IState) => state.timeline.gutterHighlight;

export const getTimestampSnappingMinutes = (state: IState) =>
  state.timeline.timestampSnappingMinutes;

const readSyntheticSlice = (state: { [syntheticSliceName]?: SyntheticState }) =>
  state[syntheticSliceName];

const readSyntheticMemoryEntities = (state: { [syntheticSliceName]?: SyntheticState }) =>
  (state[syntheticSliceName]?.memories.entities ?? EMPTY_OBJECT) as Record<number, Entry>;

const readSyntheticMemoryResult = (state: { [syntheticSliceName]?: SyntheticState }) =>
  (state[syntheticSliceName]?.memories.ids ?? EMPTY_ARRAY) as Array<number>;

const getSyntheticMemoryGroups = createSelector(
  [readSyntheticSlice],
  (syntheticSlice): Array<SuggestedEntry> => {
    return Object.values<SuggestedEntry>(syntheticSlice?.groups?.entities ?? EMPTY_OBJECT);
  }
);

export const getShowAiIgnoredMemoriesModal = (state: IState) =>
  state.timeline.showAiIgnoredMemoriesModal;

export const getLastPusherUpdate = (state: IState) => state.timeline.lastPusherUpdate;
export const getTimelineResultMeta = (state: IState) => state.timeline.resultMeta;

/**
 * Returns `Entry` entities
 * Combines Timely Memories with TIC Memories and Synthetic Memories
 */
export const getTimelineEntities = createSelector(
  [readTimelineEntities, readSyntheticMemoryEntities, getInjectSyntheticData],
  (entities, syntheticEntities, injectSyntheticData) => {
    if (!injectSyntheticData || !Object.keys(syntheticEntities).length) {
      return entities;
    }

    return { ...syntheticEntities, ...entities };
  }
);

/**
 * Returns `result` array of Entry IDs to display on Timeline
 * Combines Timely, TIC and Synthetic Memories
 */
export const getTimelineResult = createSelector(
  [readTimelineResult, readSyntheticMemoryResult, getInjectSyntheticData],
  (result, syntheticResult, injectSyntheticData) => {
    return result.concat(injectSyntheticData ? syntheticResult : EMPTY_ARRAY);
  }
);
export const getTimelineSelection = readTimelineSelection;
export const getScrollPosition = readScrollPosition;
export const getTimelineGutterHighlight = readTimelineGutterHighlight;

export const getTimelineEntries = createSelector(
  [getTimelineEntities, getTimelineResult],
  (entities, ids) => ids.map(id => entities[id])
);

/**
 * Returns the computed filters. Store is overwritten by user's preferences.
 */
export const getFilter = createSelector(
  [readFilter, getUserTimeFormat, getUserProperties],
  (filter, timeFormat, userProperties): Filter => {
    // Maybe TODO: zod the overwrites to make sure they match Filter schema, to avoid the `as any`
    const overwrites = pick(userProperties, ...TIMELINE_FILTERS_TO_REMEMBER) as any;

    return {
      ...filter,
      ...overwrites,
      timeFormat: timeFormat || filter.timeFormat,
    };
  }
);

export type MemoryViewmode =
  | {
      tic: true;
      viewmode: TicMemoryViewmode;
    }
  | {
      tic: false;
      viewmode: Filter['mode'];
    };
export const getMemoryViewmode = createSelector(
  [getFilter, getHasEnabledTic, getTicMemoryViewmode],
  (filter, hasEnabledTic, ticMemoryViewmode): MemoryViewmode => {
    if (hasEnabledTic) {
      return { tic: true, viewmode: ticMemoryViewmode };
    }
    return { tic: false, viewmode: filter.mode };
  }
);

export const getTimelineXhrStatusState = createSelector(
  [readXhrStatus],
  xhrStatus => xhrStatusUIState(xhrStatus),
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);
export const getTimelineXhrStatusStateByKey = createSelector(
  [readXhrStatus, (_: unknown, props: { key?: string }) => props.key],
  (xhrStatus, key) => xhrStatusUIState(xhrStatus, key),
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getMemoryEntries = getTimelineEntries;

/**
 * Returns `SuggestedEntry` aka Memory Groups from Timely & TIC sources
 */
export const getGroups = readGroups;

export const getMemoryGroups = createSelector(
  [getGroups, getSyntheticMemoryGroups, getInjectSyntheticData],
  (groups, syntheticGroups, injectSyntheticMemories): Array<SuggestedEntry> => {
    if (!injectSyntheticMemories || !syntheticGroups.length) {
      return groups;
    }

    return syntheticGroups.concat(groups);
  }
);

/**
 * Embeds 3 full "predicted" project payloads onto the SuggestedEntries
 * If less than 3 predictions, include the most recent projects too.
 *
 * NB: Multiple .slices for code clarity, "optimizing" this made it harder to read
 */
export const getMemoryGroupsWithProjects = createSelector(
  [getMemoryGroups, getProjectVariantMap, getRecentlyUsedProjectIds, getPinnedProjectIdsSet],
  (
    groups,
    projectVariantMap,
    recentProjectIds,
    pinnedProjectIdsSet
  ): Array<SuggestedEntryWithProjects> => {
    const maxProjects = 3;
    const groupsWithProjects = groups.map((group): SuggestedEntryWithProjects => {
      const predictedProjectIds: Array<number> = [];
      const predictedProjects = group.projects
        .slice(0, maxProjects)
        .map(({ project_id, accuracy }): SuggestedEntryProject | null => {
          const variant = projectVariantMap[project_id];
          if (variant == null) {
            return null;
          }
          const project = enrichProjectVariant(variant, pinnedProjectIdsSet);
          predictedProjectIds.push(project_id); // To avoid duplicates in recentProjects below
          return { accuracy, ...project };
        })
        .filter(notNull)
        .sort((a, b) => b.accuracy - a.accuracy);

      // Pad the projects list with recently used projects, if we don't have sufficient predictions
      // Make sure to avoid duplicates too.
      const recentProjects =
        predictedProjects.length === maxProjects
          ? EMPTY_ARRAY
          : recentProjectIds
              .filter(id => !predictedProjectIds.includes(id))
              .slice(0, maxProjects)
              .map(project_id => {
                const variant = projectVariantMap[project_id];
                if (variant == null) {
                  return null;
                }
                const project = enrichProjectVariant(variant, pinnedProjectIdsSet);
                return { accuracy: 0, ...project };
              })
              .filter((x): x is SuggestedEntryProject => x != null);

      return {
        ...group,
        projects: [...predictedProjects, ...recentProjects].slice(0, maxProjects),
      };
    });

    return groupsWithProjects.length ? groupsWithProjects : EMPTY_ARRAY;
  }
);

export const getProjectPredictions = readTimelineProjectPredictions;

export const getEntryIdPredictions = readTimelineEntryIdPredictions;

export const getTimelineStyle = createSelector(
  [readTimelineStyle],
  (selectedStyle): TimelineStyleTypes => {
    if (!selectedStyle) {
      return TimelineStyleTypes.timespansOnTimeline;
    }

    return selectedStyle;
  }
);

interface SubscriptionTimelineMessage {
  block1?: string;
  block2?: string;
  block3?: string;
  ctaText?: string;
}

const getSubscriptionPromptCopy = (
  isTrialEnded: boolean,
  isSubscriptionExpired: boolean,
  nextCharge: string,
  canUserUpgradeSubscription: boolean,
  isEnterprisePlan: boolean
): SubscriptionTimelineMessage | null => {
  if (!canUserUpgradeSubscription) {
    const contactPerson = isEnterprisePlan ? 'account manager' : 'account admin';

    if (isTrialEnded) {
      return {
        block1: `Your free trial has ended. Please contact your ${contactPerson} to continue using Timely.`,
        block2:
          'Don’t worry, we’ll keep recording your memories so everything will be up and running when you come back!',
      };
    }
    if (isSubscriptionExpired) {
      return {
        block1: `Your subscription has been cancelled. Please contact your ${contactPerson} to continue using Timely.`,
        block2:
          'Don’t worry, we’ll keep recording your memories so everything will be up and running when you come back!',
      };
    }
  }

  if (canUserUpgradeSubscription) {
    if (isTrialEnded) {
      return {
        block2: `Your trial period has ended on ${DateTime.fromISO(nextCharge).toLocaleString(
          DateTime.DATE_FULL
        )} but don’t worry, we’ll keep recording your memories so everything will be up and running when you come back!`,
        block3: 'Upgrade now to continue using Timely to its full potential!',
        ctaText: 'Select your plan now',
      };
    }

    if (isSubscriptionExpired) {
      return {
        block1: 'Whoops!',
        block2: `Your subscription was cancelled on ${DateTime.fromISO(nextCharge).toLocaleString(
          DateTime.DATE_FULL
        )} but don’t worry, we’ll keep recording your memories so everything will be up and running when you come back!`,
        block3: 'Upgrade now to continue using Timely to its full potential!',
        ctaText: 'Update my subscription',
      };
    }
  }

  return null;
};

export const getTimelineSubscriptionPrompt = createSelector(
  [getIsTrialEnded, getIsSubscriptionExpired, getCurrentAccount, getUpgradePermissionsInfo],
  (isTrialEnded, isSubscriptionExpired, account, upgradePermissions) => {
    const copy = getSubscriptionPromptCopy(
      isTrialEnded,
      isSubscriptionExpired,
      account.next_charge,
      upgradePermissions.canUserUpgradeSubscription,
      account.plan_custom
    );
    const messageKey: SubscriptionMessageKeyTypes = isTrialEnded
      ? 'trialEnded'
      : 'subscriptionExpired';
    return { copy, upgradePermissions, messageKey };
  }
);

export const getAiIgnoredMemories = createSelector([getTimelineEntries], memories => {
  const aiIgnoredMemories = memories.filter(memory => memory.spam && memory.updated_by === 'AI');
  return aiIgnoredMemories.length ? aiIgnoredMemories : EMPTY_ARRAY;
});

export const getTimelineSettings = createSelector(
  [getTimestampSnappingMinutes],
  timestampSnappingMinutes => {
    return {
      timestampSnappingMinutes,
    };
  }
);
